import { store } from "@app/store"
import { selectAccessToken } from "@features/login/loginSelectors"
import {
  Coverage,
  CreateRuleRequest,
  Dashboard,
  Preview,
  Replay,
  ReplayRequest,
  RoutingSettings,
  Rule,
  UpdateRuleRequest,
} from "@features/lead-routing/leadRouting.types"

// Hand-written fetch client for the lead-routing endpoints. Follows the same
// pattern as TrackClient: the generated @masterschool/course-builder-api client
// predates these routes, so we call them directly and inject the bearer token.
function basePath(): string {
  return `${import.meta.env.VITE_BASE_URL ?? ""}/api/v1/lead-routing`
}

function apiBase(): string {
  return `${import.meta.env.VITE_BASE_URL ?? ""}/api`
}

function headers(): Record<string, string> {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${selectAccessToken(store.getState())}`,
  }
}

async function parse<T>(response: Response, action: string): Promise<T> {
  if (!response.ok) {
    throw new Error(
      `Failed to ${action} (${response.status} ${response.statusText})`,
    )
  }
  return response.json()
}

export type MyRouting = {
  consultant: {
    name: string
    isAvailable: boolean
    acceptedQtfs: number[]
    languages: string[]
  } | null
  onShiftNow: boolean
  minutesToNextShift: number | null
  rules: { ruleId: string; ruleName: string; targetPct: number }[]
  receivedCount: number
  received: {
    leadName: string | null
    email: string | null
    routedAt: string
    source: string
    ruleName: string | null
    confirmed: boolean
    qtfScore: number | null
    program: string | null
    propertiesSource: string | null
    propertiesSyncedAt: string | null
  }[]
}

export type RuleLeadCounts = {
  total: number
  everyoneElse: number
  rules: { ruleId: string; count: number }[]
}

export type RecentRoutedLead = {
  leadId: string
  leadName: string | null
  email: string | null
  routedAt: string
  source: string
  ruleName: string | null
  ruleIsFallback?: boolean
  consultantName: string | null
  snapshot: Record<string, unknown>
}

export type RuleDistribution = {
  total: number
  consultants: {
    consultantId: string | null
    consultantName: string | null
    count: number
  }[]
}

export class LeadRoutingClient {
  static async getMyRouting(from: string, to: string): Promise<MyRouting> {
    const response = await fetch(
      `${apiBase()}/v1/my-routing?from=${from}&to=${to}`,
      { headers: headers() },
    )
    return parse(response, "load your routing view")
  }

  static async getRuleLeadCounts(
    from: string,
    to: string,
  ): Promise<RuleLeadCounts> {
    const response = await fetch(
      `${basePath()}/rule-lead-counts?from=${from}&to=${to}`,
      { headers: headers() },
    )
    return parse(response, "load rule lead counts")
  }

  static async listRecentRoutedLeads(
    from: string,
    to: string,
    page: number,
    pageSize: number,
  ): Promise<{ rows: RecentRoutedLead[]; total: number }> {
    const response = await fetch(
      `${basePath()}/recent-leads?from=${from}&to=${to}&page=${page}&pageSize=${pageSize}`,
      { headers: headers() },
    )
    return parse(response, "load recent leads")
  }

  // The real leads behind one rule's "took N%" number, or the leads that fell
  // through to Everyone else (ruleId = "fallback"). Same window as the counts.
  static async listRoutedLeadsForRule(
    ruleId: string,
    from: string,
    to: string,
  ): Promise<RecentRoutedLead[]> {
    const response = await fetch(
      `${basePath()}/rule-leads?ruleId=${ruleId}&from=${from}&to=${to}`,
      { headers: headers() },
    )
    return parse(response, "load routed leads for rule")
  }

  // The real split a rule produced: how many of its routed leads each consultant
  // actually got over the range (ruleId = "fallback" for the rotation).
  static async getRuleDistribution(
    ruleId: string,
    from: string,
    to: string,
  ): Promise<RuleDistribution> {
    const response = await fetch(
      `${basePath()}/rule-distribution?ruleId=${ruleId}&from=${from}&to=${to}`,
      { headers: headers() },
    )
    return parse(response, "load rule distribution")
  }

  static async getLeadSampleStatus(): Promise<{
    leads: number
    lastSyncedAt: string | null
  }> {
    const response = await fetch(`${basePath()}/lead-sample-status`, {
      headers: headers(),
    })
    return parse(response, "load lead sample status")
  }

  static async resyncLeadSample(): Promise<{
    leadsSynced: number
    syncedAt: string
  }> {
    const response = await fetch(`${basePath()}/resync-lead-sample`, {
      method: "POST",
      headers: headers(),
    })
    return parse(response, "resync lead sample")
  }

  // Dynamic routing properties (keys beyond the curated set) seen on recent
  // leads, with their observed values — powers the editor's property dropdown.
  static async listDynamicLeadProperties(): Promise<
    { key: string; values: string[] }[]
  > {
    const response = await fetch(`${basePath()}/lead-properties`, {
      headers: headers(),
    })
    return parse(response, "load dynamic lead properties")
  }

  static async listRules(): Promise<Rule[]> {
    const response = await fetch(`${basePath()}/rules`, {
      headers: headers(),
    })
    return parse(response, "load routing rules")
  }

  static async createRule(body: CreateRuleRequest): Promise<Rule> {
    const response = await fetch(`${basePath()}/rules`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify(body),
    })
    return parse(response, "create routing rule")
  }

  static async updateRule(id: string, body: UpdateRuleRequest): Promise<Rule> {
    const response = await fetch(`${basePath()}/rules/${id}`, {
      method: "PUT",
      headers: headers(),
      body: JSON.stringify(body),
    })
    return parse(response, "update routing rule")
  }

  static async deleteRule(id: string): Promise<void> {
    const response = await fetch(`${basePath()}/rules/${id}`, {
      method: "DELETE",
      headers: headers(),
    })
    if (!response.ok) {
      throw new Error(
        `Failed to delete routing rule (${response.status} ${response.statusText})`,
      )
    }
  }

  static async reorderRules(orderedRuleIds: string[]): Promise<Rule[]> {
    const response = await fetch(`${basePath()}/rules/reorder`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify({ orderedRuleIds }),
    })
    return parse(response, "reorder routing rules")
  }

  static async publishRule(id: string): Promise<Rule> {
    const response = await fetch(`${basePath()}/rules/${id}/publish`, {
      method: "POST",
      headers: headers(),
    })
    return parse(response, "publish routing rule")
  }

  static async setRuleDisabled(id: string, disabled: boolean): Promise<Rule> {
    const response = await fetch(
      `${basePath()}/rules/${id}/${disabled ? "disable" : "enable"}`,
      { method: "POST", headers: headers() },
    )
    return parse(
      response,
      disabled ? "disable routing rule" : "enable routing rule",
    )
  }

  static async getSettings(): Promise<RoutingSettings> {
    const response = await fetch(`${basePath()}/settings`, {
      headers: headers(),
    })
    return parse(response, "load routing settings")
  }

  static async updateSettings(
    body: Partial<RoutingSettings>,
  ): Promise<RoutingSettings> {
    const response = await fetch(`${basePath()}/settings`, {
      method: "PUT",
      headers: headers(),
      body: JSON.stringify(body),
    })
    return parse(response, "update routing settings")
  }

  static async getDashboard(windowDays: number): Promise<Dashboard> {
    const response = await fetch(
      `${basePath()}/dashboard?windowDays=${windowDays}`,
      { headers: headers() },
    )
    return parse(response, "load dashboard")
  }

  // Preview lives on the allocations controller (it reuses the eligibility engine).
  static async getPreview(): Promise<Preview> {
    const response = await fetch(
      `${import.meta.env.VITE_BASE_URL ?? ""}/api/v1/allocations/preview`,
      { headers: headers() },
    )
    return parse(response, "load preview")
  }

  // Replay a draft rule against recent real leads (validate before publishing).
  static async replayRule(body: ReplayRequest): Promise<Replay> {
    const response = await fetch(
      `${import.meta.env.VITE_BASE_URL ?? ""}/api/v1/allocations/replay`,
      { method: "POST", headers: headers(), body: JSON.stringify(body) },
    )
    return parse(response, "replay rule")
  }

  // First-match coverage & overlap of the published rules over recent leads.
  static async getRuleCoverage(limit = 300): Promise<Coverage> {
    const response = await fetch(
      `${
        import.meta.env.VITE_BASE_URL ?? ""
      }/api/v1/allocations/rule-coverage?limit=${limit}`,
      { headers: headers() },
    )
    return parse(response, "load rule coverage")
  }
}
